import { Card, Flex, Input, Label, Span, Switch, Modal, Button, toast } from '@fareye/ui'
import { useTranslation } from '@fareye/common'
import { cancelToken, post } from '@fareye/utils'
import React from 'react'
import { ImgWrapper, LabelField, UploadImage } from '../styled'
import { AppStore } from './../../../store'
import QRCode from 'qrcode'

function Profile() {
  const { store, updateStore } = React.useContext(AppStore)
  const [profile, setProfile] = React.useState<any>({})
  const [img, setImg] = React.useState<any>(null)
  const [error, setError] = React.useState<any>(null)
  const [twoFAEnabled, setTwoFAEnabled] = React.useState<boolean>(false)
  const [showOtpModal, setShowOtpModal] = React.useState<boolean>(false)
  const [otpQrDataUrl, setOtpQrDataUrl] = React.useState<string | null>(null)
  const [secretKey, setSecretKey] = React.useState<string | null>(null)
  const [otp, setOtp] = React.useState<string>('')
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false)
  const source = cancelToken().source()
  const { t } = useTranslation()

  React.useEffect(() => {
    if (store?.header?.userProfile && Object.keys(store?.header?.userProfile).length > 0) {
      setTwoFAEnabled(Boolean(store?.header?.userProfile?.twoFactorEnabled))
    }
  }, [store?.header?.userProfile])

  async function uploadImg() {
    let payload = new FormData()
    payload.append('brand_image', img)
    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
    try {
      const { status } = await post(
        '/api/v1/upload_brand_image',
        payload,
        source.token,
        headers
      )
      if (status === 200) {
        store.header?.getUserProfile()
      }
    } catch (e) {
      console.error('App Error uploading image::', e)
    }
  }

  function onChangeUpload(e: any) {
    const files = e.target.files
    if (files[0].size < 1024 * 1024) {
      var reader = new FileReader()
      reader.onloadend = () => {
        setImg(files[0])
        updateStore({
          ...store,
          header: {
            ...store.header,
            img: reader.result || store.header.img
          }
        })
        setProfile({ ...profile, logo: reader.result })
      }
      reader.readAsDataURL(files[0])
      setError(null)
    } else {
      setError('Max image size is 1MB.')
    }
  }

  const {
    email = '-',
    mName = '-',
    lastName = '-',
    username = '-',
    firstName = '-',
    primaryPhoneNo = '-'
  } = store?.header?.userProfile || {}

  async function handleEnable2FA() {
    try {
      const response = await post(
        '/api/v1/profile/enable_two_factor',
        {},
        source.token
      )
      const otpauthUrl = response?.data?.otpauth_url
      const secretKey = response?.data?.secret_key
      if (otpauthUrl) {
        const dataUrl = await QRCode.toDataURL(otpauthUrl)
        setOtpQrDataUrl(dataUrl)
        setSecretKey(secretKey)
        setShowOtpModal(true)
      }
    } catch (e: any) {
      setTwoFAEnabled(false)
      toast.error('Failed to enable two-factor authentication. Please try again.', { position: toast.POSITION.TOP_RIGHT })
    }
  }

  async function handleConfirm2FA() {
    try {
      setConfirmLoading(true)
      const response = await post(
        '/api/v1/profile/confirm_two_factor',
        { otp: otp.trim() },
        source.token
      )
      if (response?.status === 200) {
        setTwoFAEnabled(true)
        setShowOtpModal(false)
        setOtpQrDataUrl(null)
        setSecretKey(null)
        setOtp('')
        toast.success('Multi-factor authentication enabled successfully!', { position: toast.POSITION.TOP_RIGHT })
      }
    } catch (e: any) {
      setConfirmLoading(false)
      const errResponse = e?.response
      
      if (errResponse && errResponse.status === 422) {
        const errorMsg = errResponse.data?.error
        toast.error(errorMsg || 'Invalid code. Please try again.', { position: toast.POSITION.TOP_RIGHT })
        if (errorMsg && errorMsg.toLowerCase().includes('maximum') && errorMsg.toLowerCase().includes('attempts')) {
          setShowOtpModal(false)
          setOtpQrDataUrl(null)
          setSecretKey(null)
          setOtp('')
          post('/api/v1/profile/disable_two_factor', { clear_session_only: 'true' }, source.token).catch(() => {})
        } else if (errorMsg && (errorMsg.toLowerCase().includes('expired') || errorMsg.toLowerCase().includes('setup'))) {
          setShowOtpModal(false)
          setOtpQrDataUrl(null)
          setSecretKey(null)
          setOtp('')
          post('/api/v1/profile/disable_two_factor', { clear_session_only: 'true' }, source.token).catch(() => {})
        } else {
          setOtp('')
        }
      } else {
        setOtp('')
        toast.error('Invalid code. Please try again.', { position: toast.POSITION.TOP_RIGHT })
      }
    } finally {
      setConfirmLoading(false)
    }
  }

  async function handleDisable2FA() {
    try {
      const response = await post('/api/v1/profile/disable_two_factor', {}, source.token)
      if (response?.status === 200) {
        setTwoFAEnabled(false)
        setShowOtpModal(false)
        setOtpQrDataUrl(null)
        setSecretKey(null)
        toast.success('Multi-factor authentication disabled successfully!', { position: toast.POSITION.TOP_RIGHT })
      } else {
        toast.error('Failed to disable two-factor authentication. Please try again.', { position: toast.POSITION.TOP_RIGHT })
      }
    } catch (e: any) {
      setTwoFAEnabled(true)
    }
  }

  function onToggle2FA(checked: boolean) {
    if (checked) {
      handleEnable2FA()
    } else {
      handleDisable2FA()
    }
  }
  return (
    <Flex justifyContentSpaceEvenly style={{ padding: '4vmax' }}>
      <Flex column width={[1, 0.45]}>
        <Card header={t('Profile')}>
          <Flex column>
            <form
              onSubmit={(e: any) => e.preventDefault()}
              encType="multipart/form-data"
            >
              <ImgWrapper bg={store.header.backgroundColor}>
                <img src={store.header.img} />
              </ImgWrapper>
              {img ? (
                <UploadImage variant="s primary" onClick={uploadImg}>
                  {t('Submit')}
                </UploadImage>
              ) : (
                <UploadImage variant="s">
                  {t('Choose')} {t('File')}
                  <Input type="file" onChange={onChangeUpload} />
                </UploadImage>
              )}
            </form>
            {error ? (
              <Span variant="danger" p={3}>
                * {error}
              </Span>
            ) : null}
            <LabelField>{`${firstName} ${lastName}`}</LabelField>
            <LabelField>{email}</LabelField>
            {username !== email ? <LabelField>{username}</LabelField> : null}
            <LabelField>{primaryPhoneNo}</LabelField>
            <LabelField>{mName}</LabelField>
          </Flex>
        </Card>
      </Flex>
      <Flex column width={[1, 0.45]}>
        <Card header={t('Theme')}>
          <Flex alignItemsCenter>
            <Label>
              {t('Header')} {t('Color')}
            </Label>
            <Input
              type="color"
              value={store.header.backgroundColor}
              style={{ marginLeft: 10, padding: 2, height: 20 }}
              onChange={(e: any) => {
                updateStore({
                  ...store,
                  header: {
                    ...store.header,
                    backgroundColor: e.target.value
                  }
                })
              }}
            />
          </Flex>
        </Card>
        <Card header={t('Multi-Factor Authentication')} style={{ marginTop: '2vmax' }}>
          <Flex alignItemsCenter justifyContentSpaceBetween>
            <Label>{t('Enable')} MFA (Google Authenticator)</Label>
            <Switch value={twoFAEnabled} onChange={onToggle2FA} />
          </Flex>
          <Span variant="secondary" style={{ marginTop: '1vmax', fontSize: 12 }}>
            Scan the QR code with your Google Authenticator app when you enable this option.
          </Span>
        </Card>
      </Flex>
      {showOtpModal && otpQrDataUrl && (
        <Modal
          variant="m"
          toggleModal={showOtpModal}
          setToggleModal={() => {
            post('/api/v1/profile/disable_two_factor', { clear_session_only: 'true' }, source.token).catch(() => {})
            setShowOtpModal(false)
            setOtp('')
            setSecretKey(null)
          }}
        >
          <Card header={t('Scan this QR code in your Google Authenticator app')}>
            <Flex column alignItemsCenter>
              <img
                src={otpQrDataUrl}
                alt="2FA QR Code"
                style={{ width: 200, height: 200, margin: '1vmax 0' }}
              />

              {secretKey && (
                <Flex column alignItemsCenter style={{ marginTop: '0.5vmax' }}>
                  <Span variant="secondary" style={{ fontSize: 12, marginBottom: '0.5vmax' }}>
                    Or enter this secret key manually:
                  </Span>
                  <Flex 
                    alignItemsCenter 
                    justifyContentCenter 
                    style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: '8px 12px', 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      fontFamily: 'monospace',
                      fontSize: 14,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(secretKey)
                      toast.success('Secret key copied to clipboard!', { position: toast.POSITION.TOP_RIGHT })
                    }}
                  >
                    <Span>{secretKey}</Span>
                  </Flex>
                  <Span variant="secondary" style={{ fontSize: 10, marginTop: '0.5vmax' }}>
                    Click to copy
                  </Span>
                </Flex>
              )}

              <Input
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e: any) => {
                  // Only allow numbers and max 6 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                  setOtp(value)
                }}
                style={{ marginTop: '1vmax', width: 200, textAlign: 'center' }}
                maxLength={6}
              />

              <Button
                type="button"
                variant="primary m"
                style={{ marginTop: '1vmax' }}
                onClick={handleConfirm2FA}
                disabled={confirmLoading || otp.length !== 6}
              >
                Confirm
              </Button>
            </Flex>
          </Card>
        </Modal>
      )}
    </Flex>
  )
}

export default Profile
